import { Box, Breadcrumb, Flex, Text, theme } from '@/components';
import type { analysisAxisEnum } from '@/utils/enum';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  hideBreadcrumb: boolean;
  options:
    | {
        ids: string[];
        names: string[];
      }
    | undefined;
  handleGraphInteraction: (ids: string[]) => Promise<void>;
  analysisAxis: z.infer<typeof analysisAxisEnum>;
};

export const AnalysisGraphBreadcrumbs: FC<Props> = ({
  hideBreadcrumb,
  options,
  handleGraphInteraction,
  analysisAxis,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const breadcrumbItems = useMemo(() => {
    if (!options?.ids) {
      return [];
    }

    // 分析軸に応じたルートラベルを設定
    const rootLabel =
      analysisAxis === 'policy' ? t('policy') : t('organization-in-charge');

    return [
      {
        title: rootLabel,
        path: '/analysis',
        onClick: async (e) => {
          e?.preventDefault();
          await handleGraphInteraction([]);
        },
      },
      ...options.ids.map((_, idx) => {
        const isLastElement = options.ids.length - 1 === idx;
        const previousIds = options.ids.slice(0, idx + 1);
        return {
          title: options.names[idx],
          ...(!isLastElement
            ? {
                path: '/analysis',
                onClick: async (e) => {
                  e?.preventDefault();
                  await handleGraphInteraction(previousIds);
                },
              }
            : {}),
        };
      }),
    ];
  }, [options, t, handleGraphInteraction, analysisAxis]);

  return breadcrumbItems.length && !hideBreadcrumb ? (
    <Flex gap={8} vertical>
      <Text text={t('drill-down')} />
      <Box backgroundColor={token.colorBgLayout} padding={16} lineHeight={1.5}>
        <Breadcrumb items={breadcrumbItems} separator=">" />
      </Box>
    </Flex>
  ) : null;
};
